import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  UssdApplication,
  UssdApplicationsPage,
  UssdApplicationsParams,
  CreateUssdApplicationRequest,
  UpdateUssdApplicationRequest,
  PromoteUssdApplicationRequest,
  GenerateServiceCodeResponse,
  UssdPromotionRecord,
  UssdApplicationStats,
  UssdDuplicateCheck,
  UssdUsageRecord,
} from '../../types/ussd.models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class UssdApplicationsService {
  private http = inject(HttpClient);
  private baseUrl = environment.ussdApplicationsApiUrl;

  // --- Applications ---

  /**
   * One page of the organisation's applications. The backend returns a plain
   * array and puts the total in the X-Total-Count header (JHipster
   * PaginationUtil), so the page metadata is rebuilt from that header.
   */
  getApplications(
    organisationId: string,
    params: UssdApplicationsParams = {}
  ): Observable<UssdApplicationsPage> {
    const { page = 0, size = 20, sort } = params;
    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    if (sort) httpParams = httpParams.set('sort', sort);

    return this.http.get<UssdApplication[] | UssdApplicationsPage>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications`,
      { params: httpParams, observe: 'response' }
    ).pipe(
      map((response: HttpResponse<UssdApplication[] | UssdApplicationsPage>) => {
        const body = response.body;
        if (Array.isArray(body)) {
          const header = Number(response.headers.get('X-Total-Count'));
          const hasHeader = response.headers.has('X-Total-Count') && Number.isFinite(header);
          // Without the header, assume one more page whenever this one is full.
          const total = hasHeader
            ? header
            : page * size + body.length + (body.length === size ? 1 : 0);
          const totalPages = Math.max(1, Math.ceil(total / size));
          return {
            content: body,
            totalElements: hasHeader ? header : page * size + body.length,
            totalPages,
            size,
            number: page,
            first: page === 0,
            last: page >= totalPages - 1,
            numberOfElements: body.length,
            empty: body.length === 0,
          } as UssdApplicationsPage;
        }
        return (body ?? {
          content: [],
          totalElements: 0,
          totalPages: 1,
          size,
          number: page,
          first: true,
          last: true,
          numberOfElements: 0,
          empty: true,
        }) as UssdApplicationsPage;
      })
    );
  }

  /** Totals for the applications summary line. */
  getStats(organisationId: string): Observable<UssdApplicationStats> {
    return this.http.get<UssdApplicationStats>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/stats`
    );
  }

  /**
   * Whether another application in the organisation already uses this name
   * or service code. Pass `excludeId` when editing so the app is not compared
   * with itself. Read `nameExists` / `serviceCodeExists` for the one you asked
   * about.
   */
  checkDuplicate(
    organisationId: string,
    query: { name?: string; serviceCode?: string; excludeId?: string }
  ): Observable<UssdDuplicateCheck> {
    // The backend requires serviceCode (400 without it), so always send it,
    // empty when only the name is being checked.
    let params = new HttpParams().set('serviceCode', query.serviceCode ?? '');
    if (query.name) params = params.set('name', query.name);
    if (query.excludeId) params = params.set('excludeId', query.excludeId);
    return this.http.get<UssdDuplicateCheck>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/check-duplicate`,
      { params }
    );
  }

  /**
   * Subscriber sessions for the organisation between two moments. The
   * endpoint is unpaged, so always pass a bounded window.
   */
  getUsageRecords(organisationId: string, from: Date, to: Date): Observable<UssdUsageRecord[]> {
    const params = new HttpParams()
      .set('from', from.toISOString())
      .set('to', to.toISOString());
    return this.http
      .get<UssdUsageRecord[]>(
        `${this.baseUrl}/core/ussd-usage-records/by-organisation/${organisationId}`,
        { params }
      )
      .pipe(map((records) => (Array.isArray(records) ? records : [])));
  }

  getApplicationById(
    organisationId: string,
    applicationId: string
  ): Observable<UssdApplication> {
    return this.http.get<UssdApplication>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}`
    );
  }

  createApplication(
    organisationId: string,
    request: CreateUssdApplicationRequest
  ): Observable<UssdApplication> {
    return this.http.post<UssdApplication>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications`,
      request
    );
  }

  updateApplication(
    organisationId: string,
    applicationId: string,
    request: UpdateUssdApplicationRequest
  ): Observable<UssdApplication> {
    return this.http.patch<UssdApplication>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}`,
      request
    );
  }

  enableApplication(
    organisationId: string,
    applicationId: string
  ): Observable<UssdApplication> {
    return this.http.post<UssdApplication>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}/enable`,
      {}
    );
  }

  disableApplication(
    organisationId: string,
    applicationId: string
  ): Observable<UssdApplication> {
    return this.http.post<UssdApplication>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}/disable`,
      {}
    );
  }

  deleteApplication(
    organisationId: string,
    applicationId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}`
    );
  }

  promoteApplication(
    organisationId: string,
    applicationId: string,
    request: PromoteUssdApplicationRequest
  ): Observable<UssdPromotionRecord> {
    // Returns the promotion record, not the application: PRODUCTION targets
    // stay PENDING_APPROVAL, other targets are approved straight away.
    return this.http.post<UssdPromotionRecord>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}/promote`,
      request
    );
  }

  getPromotionHistory(
    organisationId: string,
    applicationId: string
  ): Observable<UssdPromotionRecord[]> {
    return this.http.get<UssdPromotionRecord[]>(
      `${this.baseUrl}/organisations/${organisationId}/ussd/applications/${applicationId}/promotions`
    );
  }

  generateServiceCode(countryCode: string): Observable<GenerateServiceCodeResponse> {
    const params = new HttpParams().set('countryCode', countryCode);
    return this.http.post<GenerateServiceCodeResponse>(
      `${this.baseUrl}/ussd/application/generate-simulation-code`,
      null,
      { params }
    );
  }
}
